import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useContext, useState, useEffect, useMemo } from 'react';
import {getUsersTemplate, putUsersTemplate} from "../../../../services/template/Templates";
import {AuthContext} from "../../../auth/providers/AuthProvider";
import {CsrfContext} from "../../../../providers/CsrfProvider";
import {TemplateContext} from "../../../../providers/TemplateProvider";

const UseTemplateModel = (id) => {
    const { authClaims } = useContext(AuthContext);
    const { csrfToken } = useContext(CsrfContext);
    const { ownerSub,setOwnerSub } = useContext(TemplateContext);
    const queryClient = useQueryClient();

    const normalizedId = useMemo(() => String(id), [id]);

    const queryFn = useMemo(() => {
        return () => getUsersTemplate(normalizedId, csrfToken);
    }, [normalizedId, csrfToken]);

    const { data, isLoading, isError } = useQuery(
        ['templates', normalizedId],
        queryFn,
        {
            refetchOnMount: false, // Use cached data when TemplateModel mounts; ViewTemplates/ParticipantsModel already fetch
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
            cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
            enabled: !!normalizedId, // Only run query if we have an ID
        }
    );

    // Initialize formData - check cache first, then use query data, then defaults
    const cachedData = queryClient.getQueryData(['templates', normalizedId]);
    const initialData = data || cachedData || { name: 'loading...', description: '', typical_cost: 0, owner_sub: '' };

    const [formData, setFormData] = useState(initialData);
    const [initialFormData, setInitialFormData] = useState(initialData);

    // Update formData when query data changes (handles both initial load and cached data)
    useEffect(() => {
        if (data) {
            setFormData(data);
            setInitialFormData(data);
            if (data.owner_sub) {
                setOwnerSub(data.owner_sub);
            }
        } else {
            // Check cache again in case it wasn't available during initial render
            const latestCachedData = queryClient.getQueryData(['templates', normalizedId]);
            if (latestCachedData && (!formData || formData.name === 'loading...')) {
                setFormData(latestCachedData);
                setInitialFormData(latestCachedData);
                if (latestCachedData.owner_sub) {
                    setOwnerSub(latestCachedData.owner_sub);
                }
            }
        }
    }, [data, setOwnerSub, normalizedId, queryClient]);

    // Check if form has changes
    const hasChanges = useMemo(() => {
        if (!initialFormData || !formData) return false;
        return (
            initialFormData.name !== formData.name ||
            initialFormData.description !== formData.description ||
            initialFormData.typical_cost !== formData.typical_cost ||
            initialFormData.group_id !== formData.group_id
        );
    }, [initialFormData, formData]);

    // Mutation for submitting form data
    const mutation = useMutation(putUsersTemplate(csrfToken), {
        onSuccess: () => {
            // Invalidate queries to refetch after mutation
            queryClient.invalidateQueries(['templates', normalizedId]);
            queryClient.invalidateQueries("templates");
        },
    });

    const handleSubmit = (data) => {
        // Ensure archived field is included based on archived_at if not explicitly set
        const submitData = {
            ...data,
            id: normalizedId,
            archived: data.archived !== undefined ? data.archived : (data.archived_at != null && data.archived_at !== '')
        };
        mutation.mutate(submitData);
    };

    return {
        formData,
        setFormData,
        isLoading,
        isError,
        handleSubmit,
        mutation,
        hasChanges
    };
};
export default UseTemplateModel;
